import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "../api/axios";
import userStore from "../../../zustand/userStore";
import AllFeatureDataTable from "../../../GuiComponents/AllFeatureDataTable.table";

/**
 * Leaderboard — dynamic 3-person podium.
 *
 * Usage:
 *   <Leaderboard
 *     title="Leaderboard"
 *     subtitle="Celebrating our top performers of DocMaster's Legal Mastering Quiz"
 *     users={[
 *       { position: 1, name: "Bhavesh Jain", score: 1, avatarUrl: "" },
 *       { position: 2, name: "Nancy",        score: 2, avatarUrl: "" },
 *       { position: 3, name: "Abhijit Singh", score: 3, avatarUrl: "" },
 *     ]}
 *   />
 *
 * - `users` can be given in any order; they're re-sorted by `position` internally.
 * - `score` is shown inside the gradient bar under each name.
 * - `avatarUrl` is optional — a placeholder silhouette is used if omitted.
 * - Pass 1, 2, or 3 users. With fewer than 3, missing podium slots collapse gracefully.
 */

const BADGE_COLORS = {
  1: "#f5a623",
  2: "#f5a623",
  3: "#f5a623",
  // 2: "#c9ced6",
  // 3: "#e08a4a",
};

const PODIUM_HEIGHTS = {
  1: 140,
  2: 70,
  3: 70,
};

function Avatar({ name, avatarUrl, position }) {
  return (
    <div style={styles.avatarWrap}>
      <div style={styles.avatarCircle}>
        {avatarUrl ? (
          <img src={avatarUrl} alt={name} style={styles.avatarImg} />
        ) : (
          <svg viewBox="0 0 24 24" width="34" height="34" fill="none">
            <circle cx="12" cy="8" r="4" fill="#9aa1ac" />
            <path d="M4 20c0-4.4 3.6-7 8-7s8 2.6 8 7" fill="#9aa1ac" />
          </svg>
        )}
      </div>
      <div
        style={{
          ...styles.badge,
          background: BADGE_COLORS[position] || "#c9ced6",
        }}
      >
        {position}
      </div>
    </div>
  );
}

export function TopLeaderboard({
  title = "Leaderboard",
  users = [],
  emptyMessage = "No Live quiz found",
}) {
  const sorted = Array.isArray(users)
    ? [...users].sort((a, b) => a.position - b.position)
    : [];

  const byPos = (p) => sorted.find((u) => u.position === p);

  // Podium visual order: 2nd (left), 1st (center), 3rd (right)
  const podiumOrder = [byPos(2), byPos(1), byPos(3)];

  const hasAnyUser = sorted.length > 0;
  console.log("sorted.length", sorted.length);

  return (
    <>
      {hasAnyUser ? (
        <div style={styles.card}>
          <div style={styles.header}>
            {/* <span style={styles.trophy} role="img" aria-label="trophy">
              🏆
            </span> */}
            <div style={styles.avatarWrap}>
              <div style={styles.crown}>👑</div>
            </div>
            <h2 style={styles.title}>{title}</h2>
          </div>

          <div style={styles.podiumRow}>
            {podiumOrder.map((user, i) =>
              user ? (
                <div key={user.position} style={styles.slot}>
                  <Avatar
                    name={user.name}
                    avatarUrl={user.avatarUrl}
                    position={user.position}
                  />

                  <div style={styles.name} title={user.name}>
                    {user.name}
                  </div>

                  <div
                    style={{
                      ...styles.scoreBar,
                      height: PODIUM_HEIGHTS[user.position] || 60,
                    }}
                  >
                    {user.score}
                  </div>
                </div>
              ) : (
                <div key={`empty-${i}`} style={styles.slot} />
              ),
            )}
          </div>
        </div>
      ) : (
        <p style={styles.emptyMessage}>{emptyMessage}</p>
      )}
    </>
  );
}

const styles = {
  card: {
    width: 340,
    borderRadius: 16,
    background: "#ffffff",
    padding: "24px 20px 28px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    textAlign: "center",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  trophy: { fontSize: 22 },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 700,
    color: "#1c1c1e",
  },
  subtitle: {
    margin: "8px 0 20px",
    fontSize: 13.5,
    lineHeight: 1.4,
    color: "#6b7280",
  },
  podiumRow: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: 14,
  },
  slot: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: 92,
  },
  avatarWrap: {
    position: "relative",
    marginBottom: 8,
  },
  crown: {
    position: "absolute",
    top: -45,
    left: "50%",
    transform: "translateX(-50%)",
    color: "#f9b233",
    fontSize: "2.2rem",
    animation: "crown-pulse 1.5s ease-in-out infinite",
  },
  avatarCircle: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "#e9ebef",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  avatarImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  badge: {
    position: "absolute",
    top: -4,
    right: -4,
    width: 22,
    height: 22,
    borderRadius: "50%",
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px solid #fff",
  },
  name: {
    fontSize: 13,
    fontWeight: 600,
    color: "#1c1c1e",
    marginBottom: 8,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  scoreBar: {
    width: "100%",
    borderRadius: 10,
    background: "#2563eb",
    color: "#fff",
    fontSize: 22,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyMessage: {
    marginTop: 24,
    color: "#e05555",
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center",
  },
};

// Small inline icons so we don't need to pull in an icon font just for two glyphs.
const CheckIcon = ({ color }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="3"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const XIcon = ({ color }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="3"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ChevronIcon = ({ open }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#888"
    strokeWidth="2"
    style={{
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
      transition: "transform 0.15s ease",
      flexShrink: 0,
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// One row of the accordion: collapsed header (status, question, time) + expandable option list.
const QuestionAccordionItem = ({ q, idx, isOpen, onToggle }) => {
  const notAttempted = !q.selected_option;
  const isCorrect = !notAttempted && q.selected_option === q.correct_option;

  // Backend may not send per-question option text yet. Fall back to just the letters
  // so this still renders sensibly until `q.options` (e.g. {A: "...", B: "..."}) exists.
  const optionEntries = q.options
    ? Array.isArray(q.options)
      ? q.options.map((option) => [option.label, option.content])
      : Object.entries(q.options).map(([letter, option]) => [
          option && typeof option === "object"
            ? option.label || letter
            : letter,
          option && typeof option === "object" ? option.content : option,
        ])
    : [q.correct_option, q.selected_option]
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .map((letter) => [letter, null]);

  const statusBg = notAttempted ? "#FFF4E5" : isCorrect ? "#E6F7EC" : "#FDEBEB";
  const statusColor = notAttempted
    ? "#B7791F"
    : isCorrect
      ? "#1E8E4F"
      : "#C53030";

  return (
    <div
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        overflow: "hidden",
        marginBottom: "8px",
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "10px 12px",
          background: "#fff",
          border: "none",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            width: "22px",
            height: "22px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            background: statusBg,
          }}
        >
          {notAttempted ? (
            <span
              style={{ fontSize: "10px", fontWeight: 700, color: statusColor }}
            >
              –
            </span>
          ) : isCorrect ? (
            <CheckIcon color={statusColor} />
          ) : (
            <XIcon color={statusColor} />
          )}
        </span>
        <span style={{ flex: 1, fontSize: "14px", color: "#1a1a1a" }}>
          Q{idx + 1}. {q.question}
        </span>
        <span style={{ fontSize: "12px", color: "#888", flexShrink: 0 }}>
          {q.time_taken_ms
            ? `${(q.time_taken_ms / 1000).toFixed(2)} sec`
            : "N/A"}
        </span>
        <ChevronIcon open={isOpen} />
      </button>

      {isOpen && (
        <div style={{ padding: "4px 12px 12px 44px", background: "#fff" }}>
          {notAttempted && (
            <p
              style={{ fontSize: "13px", color: "#B7791F", margin: "0 0 8px" }}
            >
              Not attempted
            </p>
          )}
          {optionEntries.map(([letter, text]) => {
            const isCorrectOpt = letter === q.correct_option;
            const isSelectedWrong = letter === q.selected_option && !isCorrect;
            let bg = "transparent";
            let color = "#333";
            let icon = null;
            if (isCorrectOpt) {
              bg = "#E6F7EC";
              color = "#1E8E4F";
              icon = <CheckIcon color="#1E8E4F" />;
            } else if (isSelectedWrong) {
              bg = "#FDEBEB";
              color = "#C53030";
              icon = <XIcon color="#C53030" />;
            }
            return (
              <div
                key={letter}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "6px 10px",
                  marginBottom: "4px",
                  borderRadius: "6px",
                  background: bg,
                  color: color,
                  fontSize: "13px",
                }}
              >
                <span style={{ fontWeight: 600, marginRight: "8px" }}>
                  {letter}.
                </span>
                <span style={{ flex: 1 }}>
                  {text || "(answer text not returned by API)"}
                </span>
                {icon && <span style={{ marginLeft: "6px" }}>{icon}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, tone }) => {
  const tones = {
    neutral: { bg: "#F5F5F4", text: "#1a1a1a" },
    success: { bg: "#E6F7EC", text: "#1E8E4F" },
    danger: { bg: "#FDEBEB", text: "#C53030" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div
      style={{
        background: t.bg,
        borderRadius: "8px",
        padding: "10px",
        textAlign: "center",
      }}
    >
      <p
        style={{
          fontSize: "12px",
          color: t.text,
          opacity: 0.8,
          margin: "0 0 4px",
        }}
      >
        {label}
      </p>
      <p
        style={{ fontSize: "18px", fontWeight: 600, color: t.text, margin: 0 }}
      >
        {value}
      </p>
    </div>
  );
};

const CompletedQuizzes2 = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [popUp, setPopUp] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [openIdx, setOpenIdx] = useState(0); // first question expanded by default
  const userData = userStore((state) => state.user);
  const [quizType, setQuizType] = useState("");
  // const location = useLocation();
  // const state = location.state;
  // // const state = location.state || JSON.parse(sessionStorage.getItem("currentQuiz")) || {};
  // const { quizType, quiz_id } = state || {};

  useEffect(() => {
    fetchCompleted();
  }, []);

  const fetchCompleted = async () => {
    try {
      const res = await axios.get(
        // `/participant/completed-quizzes?user_id=${userData.id}`
        // `/participant/completed-quizzes?user_id=${userData.id}`,
        `/student/completed-quizzes?user_id=${userData.id}`,
      );
      setQuizzes(res.data);
    } catch (err) {
      console.error("Error fetching completed quizzes:", err);
    }
  };

  const openPopup = async (quiz) => {
    setQuizType(quiz.quiz_type);
    try {
      const res = await axios.get(
        // `/participant/results?user_id=${userData.id}&quiz_id=${quiz.id}&quiz_type=${quiz.quiz_type}`
        `/student/results?user_id=${userData.id}&quiz_id=${quiz.id}&quiz_type=${
          quiz.quiz_type
        }&class_id=${quiz.class_id || ""}&group_id=${quiz.group_id || ""}`,
      );

      setSelectedQuiz({
        ...res.data,
        quiz_title: quiz.title,
      });
      setOpenIdx(0);
    } catch (err) {
      console.error("Error fetching completed quizzes:", err);
    }
    setPopUp(true);
  };

  const closePopup = () => {
    setPopUp(false);
    setSelectedQuiz(null);
  };

  const tableColumns = [
    { accessor: "slno", Header: "#" },
    { accessor: "title", Header: "Quiz Title" },
    ...(searchTerm === "" || searchTerm === "1" || searchTerm === "3"
      ? [{ accessor: "conducted_at", Header: "Conducted Date Time" }]
      : []),
    { accessor: "score", Header: "Score" },
    { accessor: "action", Header: "Action" },
  ];
  const tableData = quizzes.map((e, index) => ({
    slno: index + 1,
    title: e.title || "-",
    conducted_at: e.start_date ? formatUTCToIST(e.start_date) : "N/A",
    quiz_type: e.quiz_type,
    score: e.score || "-",
    action: (
      <img
        width="20"
        height="20"
        src="https://img.icons8.com/ios-glyphs/30/visible--v1.png"
        alt="visible--v1"
        style={{ cursor: "pointer" }}
        onClick={() => openPopup(e)}
      />
    ),
  }));

  function formatUTCToIST(input) {
    const date = new Date(input);
    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    const parts = date.toLocaleString("en-GB", options).replace(",", "");
    const [d, t, a] = parts.split(" ");
    return `${d} at ${t} ${a.toUpperCase()}`;
  }

  console.log("selectedQuiz", JSON.stringify(selectedQuiz));

  return (
    <div>
      <h3 style={{ fontSize: "16px" }}>
        <strong>Completed Quizzes</strong>
      </h3>

      {tableData && tableData.length > 0 ? (
        <div
          style={{
            width: "100%",
            padding: "20px",
            // height: "75vh",
            // overflow: "auto",
            border: "1px solid rgb(230,233,237)",
          }}
        >
          <div style={{ display: "flex", gap: "2%", width: "100%" }}>
            <p>
              <strong>Quiz Type</strong>
            </p>{" "}
            <select
              className="form-input small-select"
              onChange={(e) => setSearchTerm(e.target.value)}
            >
              <option value="">-- Select --</option>
              <optgroup label="Live">
                <option value="1">Academic Quiz</option>
                <option value="3">Thematic Quiz</option>
              </optgroup>
              <optgroup label="Practice">
                <option value="2">Academic Quiz</option>
                <option value="4">Thematic Quiz</option>
              </optgroup>
            </select>
            <p>
              Matched Record(s) Count &nbsp;
              <strong>
                {searchTerm
                  ? tableData.filter((e) => e.quiz_type === Number(searchTerm))
                      ?.length
                  : tableData?.length}
              </strong>
            </p>
          </div>
          <br />
          <AllFeatureDataTable
            columns={tableColumns}
            data={
              searchTerm
                ? tableData.filter((e) => e.quiz_type === Number(searchTerm))
                : tableData
            }
            customPlaceHolder={"search"}
          />
        </div>
      ) : (
        <p style={{ textAlign: "center", color: "red", fontSize: "16px" }}>
          Completed Quizzes Not Found
        </p>
      )}
      <style>
        {`
    .quiz-popup-overlay {
      position: fixed;
      inset: 0;

      display: flex;
      align-items: center;
      justify-content: center;

      padding: 70px 30px 80px;

      background: rgba(15, 23, 42, 0.60);
      backdrop-filter: blur(3px);

      z-index: 99999;

      overflow: hidden;
    }

    .quiz-popup {
      position: relative;

      width: min(1100px, 94vw);

      /*
       * Shorter popup
       */
      height: min(680px, calc(100dvh - 150px));

      min-height: 500px;

      background: #fff;

      border-radius: 16px;

      box-shadow:
        0 20px 50px rgba(0, 0, 0, 0.25);

      overflow: hidden;

      display: flex;
      flex-direction: column;
    }

    .quiz-popup-content {
      flex: 1;

      min-height: 0;

      padding: 24px 28px;

      overflow-y: auto;
      overflow-x: hidden;

      scrollbar-width: thin;
    }

    .quiz-popup-content::-webkit-scrollbar {
      width: 6px;
    }

    .quiz-popup-content::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 10px;
    }

    /* HEADER */

    .quiz-popup-header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      gap: 20px;

      padding-right: 42px;

      margin-bottom: 18px;
    }

    .quiz-popup-title {
      margin: 0;

      font-size: 21px;
      font-weight: 700;

      color: #172033;

      line-height: 1.3;
    }

    .quiz-popup-rank {
      flex-shrink: 0;

      display: flex;
      align-items: center;

      background: #eef4ff;

      color: #2563eb;

      border: 1px solid #dbe7ff;

      padding: 7px 14px;

      border-radius: 20px;

      font-size: 13px;
      font-weight: 600;
    }

    /* CLOSE */

    .quiz-popup-close {
      position: absolute;

      top: 12px;
      right: 14px;

      width: 32px;
      height: 32px;

      border: none;

      border-radius: 50%;

      background: #f1f5f9;

      color: #475569;

      font-size: 22px;

      cursor: pointer;

      display: flex;
      align-items: center;
      justify-content: center;

      z-index: 10;
    }

    .quiz-popup-close:hover {
      background: #e2e8f0;
      color: #111827;
    }

    /* STATS */

    .quiz-stats-grid {
      display: grid;

      grid-template-columns:
        repeat(5, 1fr);

      gap: 12px;

      margin-bottom: 18px;
    }

    /* SECTION */

    .quiz-section {
      background: #f8fafc;

      border: 1px solid #e5eaf0;

      border-radius: 13px;

      padding: 16px;

      margin-bottom: 16px;
    }

    .quiz-section-header {
      display: flex;
      align-items: center;

      gap: 10px;

      margin-bottom: 12px;
    }

    .quiz-section-icon {
      width: 32px;
      height: 32px;

      display: flex;
      align-items: center;
      justify-content: center;

      border-radius: 8px;

      background: #eaf1ff;

      font-size: 15px;
    }

    .quiz-section-title {
      margin: 0;

      font-size: 16px;
      font-weight: 700;

      color: #1e293b;
    }

    .quiz-section-subtitle {
      margin: 2px 0 0;

      font-size: 12px;

      color: #64748b;
    }

    /* LEADERBOARD */

    .leaderboard-wrapper {
      width: 100%;

      background: #fff;

      border: 1px solid #e6ebf2;

      border-radius: 10px;

      padding: 8px;

      /*
       * Prevent unnecessary huge height
       */
      min-height: 220px;
    }

    /* QUESTIONS */

    .question-info-header {
      display: flex;

      align-items: center;

      justify-content: space-between;

      margin-bottom: 10px;
    }

    .question-info-title {
      margin: 0;

      font-size: 16px;

      font-weight: 700;

      color: #1e293b;
    }

    .question-count {
      font-size: 12px;

      color: #64748b;

      background: #f1f5f9;

      padding: 5px 9px;

      border-radius: 12px;
    }

    .question-list {
      display: flex;

      flex-direction: column;

      gap: 7px;
    }

    /* SELECT */

    .small-select {
      width: 220px !important;

      height: 40px !important;
    }

    /* TABLET */

    @media (max-width: 900px) {

      .quiz-popup-overlay {
        padding: 55px 15px 70px;
      }

      .quiz-popup {
        width: 96vw;

        height: calc(100dvh - 125px);

        max-height: 680px;
      }

      .quiz-stats-grid {
        grid-template-columns:
          repeat(3, 1fr);
      }
    }

    /* MOBILE */

    @media (max-width: 600px) {

      .quiz-popup-overlay {
        padding: 45px 8px 60px;
      }

      .quiz-popup {
        width: 100%;

        height: calc(100dvh - 105px);

        min-height: 450px;

        border-radius: 13px;
      }

      .quiz-popup-content {
        padding: 18px 12px;
      }

      .quiz-popup-header {
        flex-direction: column;

        align-items: flex-start;

        padding-right: 35px;

        gap: 8px;
      }

      .quiz-popup-title {
        font-size: 18px;
      }

      .quiz-stats-grid {
        grid-template-columns:
          repeat(2, 1fr);

        gap: 8px;
      }

      .quiz-section {
        padding: 11px;
      }

      .leaderboard-wrapper {
        padding: 5px;
        min-height: 200px;
      }

      .small-select {
        width: 100% !important;
      }
    }
  `}
      </style>
      {popUp && selectedQuiz && (
        <div className="quiz-popup-overlay" onClick={closePopup}>
          <div className="quiz-popup" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              className="quiz-popup-close"
              onClick={closePopup}
              aria-label="Close"
            >
              ×
            </button>

            <div className="quiz-popup-content">
              {/* Header */}
              <div className="quiz-popup-header">
                <div>
                  <h4 className="quiz-popup-title">
                    {selectedQuiz?.quiz_title}
                  </h4>
                </div>

                {selectedQuiz?.stats?.rank && (
                  <div className="quiz-popup-rank">
                    🏅 Rank {selectedQuiz.stats.rank}
                    {selectedQuiz?.stats?.total_participants
                      ? ` of ${selectedQuiz.stats.total_participants}`
                      : ""}
                  </div>
                )}
              </div>

              {/* Statistics */}
              <div className="quiz-stats-grid">
                <StatCard
                  label="Questions"
                  value={selectedQuiz?.stats?.total_questions ?? "-"}
                  tone="neutral"
                />

                <StatCard
                  label="Attempted"
                  value={selectedQuiz?.stats?.attempted ?? "-"}
                  tone="neutral"
                />

                <StatCard
                  label="Correct"
                  value={selectedQuiz?.stats?.correct ?? "-"}
                  tone="success"
                />

                <StatCard
                  label="Wrong"
                  value={selectedQuiz?.stats?.wrong ?? "-"}
                  tone="danger"
                />

                <StatCard
                  label="Score"
                  value={selectedQuiz?.stats?.total_score ?? "-"}
                  tone="neutral"
                />
              </div>
              {/* Leaderboard Section */}
              {quizType !== 2 && quizType !== 4 && (
                <div className="quiz-section">
                  <div className="quiz-section-header">
                    <div className="quiz-section-icon">🏆</div>

                    <div>
                      <h3 className="quiz-section-title">Top Leaderboard</h3>

                      <p className="quiz-section-subtitle">
                        Top participants and their scores
                      </p>
                    </div>
                  </div>

                  {/* <div className="leaderboard-wrapper"> */}
                  <div className="">
                    <TopLeaderboard
                      title=""
                      users={
                        Array.isArray(selectedQuiz?.leaderboard)
                          ? selectedQuiz.leaderboard.map((u) => ({
                              position: Number(u.position),
                              name: u.name,
                              score: u.score,
                              avatarUrl: u.photoUrl || "",
                            }))
                          : []
                      }
                      emptyMessage={
                        typeof selectedQuiz?.leaderboard === "string"
                          ? selectedQuiz.leaderboard
                          : "No leaderboard data available."
                      }
                    />
                  </div>
                </div>
              )}

              {/* Question Information */}
              <div className="quiz-section">
                <div className="question-info-header">
                  <div>
                    <h3 className="question-info-title">
                      Question Information
                    </h3>
                  </div>

                  {selectedQuiz?.answers?.length > 0 && (
                    <span className="question-count">
                      {selectedQuiz.answers.length} Questions
                    </span>
                  )}
                </div>

                <div className="question-list">
                  {selectedQuiz?.answers?.map((q, idx) => (
                    <QuestionAccordionItem
                      key={idx}
                      q={q}
                      idx={idx}
                      isOpen={openIdx === idx}
                      onToggle={() => setOpenIdx(openIdx === idx ? -1 : idx)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompletedQuizzes2;
